import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type VibeType = 'warm' | 'cool' | 'rich'

interface VibeContextValue {
  currentVibe: VibeType
  setVibe: (vibe: VibeType) => void
}

const STORAGE_KEY = 'clinic_glp1_vibe'
const VibeContext = createContext<VibeContextValue | undefined>(undefined)

export function VibeProvider({ children }: { children: React.ReactNode }) {
  const [currentVibe, setCurrentVibe] = useState<VibeType>('cool')
  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'warm' || stored === 'cool' || stored === 'rich') {
      setCurrentVibe(stored)
    }
    setIsHydrated(true)
  }, [])

  const setVibe = useCallback((vibe: VibeType) => {
    setCurrentVibe(vibe)
    localStorage.setItem(STORAGE_KEY, vibe)
  }, [])

  useEffect(() => {
    if (!isHydrated) return
    document.body.classList.remove('vibe-warm', 'vibe-cool', 'vibe-rich')
    document.body.classList.add(`vibe-${currentVibe}`)
    document.body.setAttribute('data-vibe', currentVibe)
  }, [currentVibe, isHydrated])

  return <VibeContext.Provider value={{ currentVibe, setVibe }}>{children}</VibeContext.Provider>
}

export function useVibe() {
  const context = useContext(VibeContext)
  if (!context) {
    throw new Error('useVibe must be used within a VibeProvider')
  }
  return context
}
